package dev.lingvo.site.components.studio.preview

import dev.lingvo.shared.ImageHeaders
import dev.lingvo.shared.ImageHeaderResult
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.render.RenderInputs
import dev.lingvo.shared.screenshots.text.Scripts
import dev.lingvo.shared.screenshots.text.DefaultFontResolver
import dev.lingvo.shared.screenshots.text.FontResolver
import dev.lingvo.shared.screenshots.render.effectiveLayers
import dev.lingvo.shared.screenshots.render.RenderMode
import dev.lingvo.site.components.studio.canvas.BitmapImage
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import org.w3c.dom.HTMLCanvasElement
import org.w3c.files.Blob
import kotlin.js.Promise
import kotlin.math.roundToInt

/** Decodes at display scale and transfers explicit bitmap ownership to the mounted preview. */
suspend fun previewBitmap(blob: Blob, key: String, width: Int? = null, height: Int? = null): BitmapImage {
    require(blob.size.toDouble() <= ScreenshotSpecs.MAX_IMAGE_BYTES) { "Preview images are limited to 8 MiB." }
    val dimensions = if (width != null && height != null) width to height else {
        val buffer = blob.asDynamic().arrayBuffer().unsafeCast<Promise<dynamic>>().await()
        val array: dynamic = js("(function(buffer) { return new Uint8Array(buffer); })")(buffer)
        val bytes = ByteArray(array.length as Int) { (array[it] as Int).toByte() }
        val header = ImageHeaders.parse(bytes)
        require(header is ImageHeaderResult.Ok) { "This preview image has invalid headers." }
        header.header.width to header.header.height
    }
    return suspendCancellableCoroutine { continuation ->
        val options: dynamic = js("({resizeQuality:'high'})")
        if (dimensions.first >= dimensions.second) options.resizeWidth = minOf(dimensions.first, 480)
        else options.resizeHeight = minOf(dimensions.second, 480)
        val promise = window.asDynamic().createImageBitmap(blob, options).unsafeCast<Promise<dynamic>>()
        promise.then<Unit>({ bitmap: dynamic ->
            if (continuation.isActive) continuation.resume(BitmapImage(key, bitmap)) else bitmap.close()
            Unit
        }, { failure -> if (continuation.isActive) continuation.resumeWithException(failure); Unit })
    }
}

/** Prepares fonts before queued drawing, never measuring while a subset is still downloading. */
class PreviewRenderer(private val looks: DesignLooksPort, private val fonts: FontResolver = looks.fonts) {
    private val renderer = looks.renderer()

    /** Loads the same stacks the engine resolves for every visible text layer. */
    suspend fun prepare(
        design: ScreenshotDesign, boardId: String, locale: String, caption: (TextContent, Int) -> String?,
    ) {
        val board = design.boards.first { it.id == boardId }
        var offset = 0
        for (page in board.pages) {
            for (layer in flattened(effectiveLayers(page, board, locale, design.schemaVersion)).filterIsInstance<TextLayer>()) {
                val local = (layer.content as? TextContent.SlotCaption)?.slot ?: layer.base.roleSlot
                val text = (layer.content as? TextContent.Literal)?.text ?: caption(layer.content, offset + local).orEmpty()
                if (text.isEmpty()) continue
                val script = looks.scripts.scriptFor(locale, text)
                val templateFamily = looks.templates().firstOrNull { it.id == design.templateId }?.families?.get(layer.style.role)
                val style = layer.style
                val stack = if (looks.fonts === DefaultFontResolver) DefaultFontResolver.stack(style.role, style.family,
                    design.brand, templateFamily, script, Scripts.hasEmoji(text), style.weight, style.italic)
                else looks.fonts.stack(style.role, style.family, design.brand, templateFamily, script, Scripts.hasEmoji(text))
                check(looks.ensureFont(stack, layer.style.weight, layer.style.italic, text)) {
                    "The caption font could not load. Retry when you are online."
                }
            }
            offset += page.slotIds.size
        }
    }

    /** Returns compact pixels and the real fit report, releasing the temporary canvas immediately. */
    fun render(
        design: ScreenshotDesign, boardId: String, slot: String, locale: String,
        captions: (TextContent, Int) -> String?, images: (ImageSource, Int) -> BitmapImage?,
    ): Thumbnail {
        val board = design.boards.first { it.id == boardId }
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.height = 240
        canvas.width = maxOf(1, (240.0 * board.canvas.width / board.canvas.height).roundToInt())
        try {
            val inputs = RenderInputs(locale, RenderMode.EDITOR, captions, images,
                looks.measurerFor(canvas), looks.segmenter, fonts, looks.scripts,
                looks.templates().firstOrNull { it.id == design.templateId }?.families.orEmpty())
            val report = renderer.renderSlot(design, boardId, slot, inputs, looks.surfaceFor(canvas))
            val page = board.pages.first { slot in it.slotIds }
            val captionReport = report.copy(texts = report.texts.filter { text ->
                val content = text.content as? TextContent.SlotCaption
                content != null && page.slotIds.getOrNull(content.slot) == slot
            })
            return Thumbnail(canvas.toDataURL("image/png"), captionReport)
        } finally {
            canvas.width = 0
            canvas.height = 0
        }
    }
}

private fun flattened(layers: List<Layer>): List<Layer> = layers.filterNot { it.base.hidden || it.base.opacity == 0.0 }.flatMap {
    if (it is GroupLayer) listOf(it) + flattened(it.children) else listOf(it)
}

/** Uses the browser's SHA-256 so thumbnail identities match canonical document provenance. */
suspend fun previewDocumentSha(canonical: String): String {
    val encode: dynamic = js("(function(text) { return new TextEncoder().encode(text); })")
    val digest = window.asDynamic().crypto.subtle.digest("SHA-256", encode(canonical)).unsafeCast<Promise<dynamic>>().await()
    val bytes: dynamic = js("(function(buffer) { return new Uint8Array(buffer); })")(digest)
    return (0 until (bytes.length as Int)).joinToString("") { (bytes[it] as Int).toString(16).padStart(2, '0') }
}
